import { useEffect } from "react";
import { themeApi } from "../lib/api";
import { applyThemeVars, DEFAULT_TOKENS } from "../lib/theme";

// Module-level cache so navigating between shells doesn't refetch the theme.
let THEME_CACHE = null;

/**
 * Mounts the "Command Center" dark theme by toggling `cc-theme` + `dark`
 * on <html> while an authenticated app / mobile shell is on screen, and
 * injects the tenant's resolved design tokens as CSS custom properties.
 *
 * A theme can target "desktop", "mobile", or "both". `surface` tells this
 * scope which shell mounted it so a mobile-only theme won't restyle the
 * desktop app (and vice-versa) — in that case we fall back to the built-in
 * default tokens for the non-targeted surface.
 *
 * Public pages (login, landing, customer pay pages) never mount this,
 * so they keep their own styling.
 */
export default function CommandThemeScope({ surface = "desktop" }) {
  useEffect(() => {
    const el = document.documentElement;
    el.classList.add("cc-theme", "dark");
    let active = true;

    const apply = (payload) => {
      if (!active || !payload?.tokens) return;
      const target = payload.target || "both";
      const applies = target === "both" || target === surface;
      applyThemeVars(applies ? payload.tokens : DEFAULT_TOKENS, el);
    };

    // Stale-while-revalidate: paint the cached theme instantly to avoid a flash,
    // then ALWAYS refetch so a newly assigned/updated theme is reflected on the
    // next app load or shell mount (and never leaks across tenants).
    if (THEME_CACHE) apply(THEME_CACHE);
    (async () => {
      try {
        const fresh = await themeApi.active();
        THEME_CACHE = fresh;
        apply(fresh);
      } catch {
        /* keep built-in / cached fallback */
      }
    })();

    return () => {
      active = false;
      el.classList.remove("cc-theme", "dark");
    };
  }, [surface]);
  return null;
}

// Allow the Theme Studio to push a freshly published theme without a reload.
export function setActiveThemeCache(payload) {
  THEME_CACHE = payload;
}

// Clear on logout so the next login (possibly a different company) fetches fresh.
export function clearThemeCache() {
  THEME_CACHE = null;
}
